import {
  type PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

import { Alert } from '@gympass/tai-chi/alert';
import Slide from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';

export interface SnackbarOptions {
  message: string;
  variant?: 'success' | 'error' | 'info' | 'warning' | 'neutral';
  duration?: number;
}

type SnackbarState = SnackbarOptions & {
  open: boolean;
};

export interface SnackbarContext {
  showSnackbar: (options: SnackbarOptions) => void;
}

export const SnackbarContext = createContext<SnackbarContext>({
  showSnackbar: () => null,
});

export function SnackbarProvider({ children }: PropsWithChildren) {
  const [snackbarState, setSnackbarState] = useState<SnackbarState>({
    open: false,
    message: '',
    variant: 'error',
    duration: 5000,
  });

  const value = useMemo(
    () => ({
      showSnackbar: (options: SnackbarOptions) => {
        setSnackbarState({
          ...snackbarState,
          ...options,
          open: true,
        });
      },
    }),
    [snackbarState],
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}

      <Snackbar
        open={snackbarState.open}
        autoHideDuration={snackbarState.duration}
        onClose={(event, reason) => {
          if (reason === 'clickaway' || reason === 'escapeKeyDown') {
            event.preventDefault();
            return;
          }

          setSnackbarState(prevState => ({ ...prevState, open: false }));
        }}
        ClickAwayListenerProps={{ mouseEvent: false }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ bottom: '7.5rem' }}
        TransitionComponent={Slide}
      >
        <Alert
          severity={snackbarState.variant}
          message={snackbarState.message}
          icon="Warning"
          size="large"
        />
      </Snackbar>
    </SnackbarContext.Provider>
  );
}

export function useSnackbar() {
  const value = useContext(SnackbarContext);
  return value;
}
